import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const YT_API_SRC = "https://www.youtube.com/iframe_api";
const YT_API_TIMEOUT_MS = 15000;

// Load the YouTube IFrame Player API exactly once across the app. The script
// calls its fixed global hook window.onYouTubeIframeAPIReady when window.YT is
// ready; we resolve the shared promise there so every waiter unblocks. Guards
// against double-injection (multiple players, remounts) and chains any
// pre-existing handler.
let ytApiPromise = null;

function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve, reject) => {
    let timer = null;
    const finish = (yt) => {
      if (timer) clearTimeout(timer);
      resolve(yt);
    };
    // Chain any pre-existing handler so we don't stomp another integration.
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      finish(window.YT);
    };
    if (!document.querySelector(`script[src="${YT_API_SRC}"]`)) {
      const tag = document.createElement("script");
      tag.src = YT_API_SRC;
      tag.onerror = () => {
        ytApiPromise = null; // allow a later mount to retry the load
        reject(new Error("YouTube API script failed to load"));
      };
      document.head.appendChild(tag);
    }
    // Don't leave waiters hanging forever if the API never calls its ready hook
    // (blocked/offline/CSP). Reset so a remount can try again.
    timer = setTimeout(() => {
      ytApiPromise = null;
      reject(new Error("YouTube API load timed out"));
    }, YT_API_TIMEOUT_MS);
  });
  return ytApiPromise;
}

const SAVE_INTERVAL_MS = 5000;

// YouTube player that resumes from `startSeconds` and reports playback position
// up:
//   - `onProgress(seconds)` every ~5s while PLAYING (poll getCurrentTime)
//   - `onFlush(seconds)` on PAUSED / ENDED and on unmount (terminal position),
//     before destroying the player
// `onProgress` is throttled by the caller; `onFlush` is immediate so the final
// position isn't swallowed by that throttle window. Both are fire-and-forget.
// The component owns nothing persistent beyond the YT.Player and its poll timer.
export default function YouTubePlayer({
  videoId,
  startSeconds = 0,
  onProgress,
  onFlush,
  title,
}) {
  const { t } = useTranslation();
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const [failed, setFailed] = useState(false);
  // Keep the latest callbacks without re-running the create effect.
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const onFlushRef = useRef(onFlush);
  onFlushRef.current = onFlush;

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    const currentSeconds = () => {
      const player = playerRef.current;
      if (!player || typeof player.getCurrentTime !== "function") return null;
      return Math.floor(player.getCurrentTime() || 0);
    };

    // Throttled stream (polling).
    const reportNow = () => {
      const seconds = currentSeconds();
      if (seconds !== null && seconds >= 0) onProgressRef.current?.(seconds);
    };
    // Immediate terminal write (pause/end/unmount).
    const flushNow = () => {
      const seconds = currentSeconds();
      if (seconds !== null && seconds >= 0) onFlushRef.current?.(seconds);
    };

    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      pollRef.current = setInterval(reportNow, SAVE_INTERVAL_MS);
    };

    // Persist the position when the tab is backgrounded / closed — React's
    // effect cleanup does not run on pagehide, so a watch-then-close-tab would
    // otherwise lose everything since the last poll. Best-effort (the request
    // may not finish on a hard unload, but visibilitychange:hidden fires early
    // enough that it usually does).
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushNow();
    };
    document.addEventListener("visibilitychange", onVisibility);

    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (event) => {
            if (startSeconds > 0) event.target.seekTo(startSeconds, true);
          },
          onError: () => {
            // Private/removed/embedding-disabled video — show the fallback.
            if (!cancelled) setFailed(true);
          },
          onStateChange: (event) => {
            const state = event.data;
            if (state === YT.PlayerState.PLAYING) {
              startPolling();
            } else {
              // PAUSED / ENDED / BUFFERING: stop polling and capture position.
              stopPolling();
              if (
                state === YT.PlayerState.PAUSED ||
                state === YT.PlayerState.ENDED
              ) {
                flushNow();
              }
            }
          },
        },
      });
    }).catch(() => {
      // API failed to load (timeout / blocked / offline) — render the fallback.
      if (!cancelled) setFailed(true);
    });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      stopPolling();
      // Final position before tearing down (best-effort; player may be gone).
      try {
        flushNow();
      } catch {
        /* player already destroyed */
      }
      const player = playerRef.current;
      playerRef.current = null;
      if (player && typeof player.destroy === "function") {
        try {
          player.destroy();
        } catch {
          /* ignore teardown races */
        }
      }
    };
    // Recreate the player only if the video changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  if (failed) {
    return (
      <div className="aspect-video w-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-900 text-center text-white shadow-sm">
        <p className="text-sm text-white/70">{t("materials.videoUnavailable")}</p>
        <a
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
        >
          {t("materials.openOnYouTube")}
        </a>
      </div>
    );
  }

  return (
    <div className="aspect-video w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-sm">
      {/* YT.Player replaces this div with its iframe. */}
      <div ref={hostRef} title={title} className="w-full h-full" />
    </div>
  );
}
